import {
  Address,
  CampaignDetail,
  CartCountResponse,
  ErrorResponse,
  RedeemResponse,
  ServiceResponse,
  Style,
  UseCampaignResponse,
} from '@bzbs/react-api-client';
import { create, StoreApi, UseBoundStore } from 'zustand';
import { campaignInterfaceType, campaignPointType, campaignType } from '../constant';
import { largeImage } from '../service/bzbs_service';
import { trackCommonEvents } from '../utils';
import { isCodeAutoUse, isPointEnough } from '../utils/campaign_utils';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';
import { useUserStore } from './user.store';

export type CampaignButtonType =
  | 'redeem'
  | 'get_points'
  | 'add_to_cart'
  | 'survey'
  | 'website'
  | 'draw'
  | 'donate'
  | 'delivery';

export type CampaignDetailButtonState = {
  enabled: boolean;
  type: CampaignButtonType;
  display: boolean;
};

export type CampaignState =
  | 'redeemable'
  | 'point_not_enough'
  | 'expired'
  | 'cool_down'
  | 'fail_in_condition'
  | 'sold_out'
  | 'redeemed';

export type AfterRedeemDestination = 'delivery' | 'draw' | 'code' | 'get_points';

export type AfterRedeem = {
  destination: AfterRedeemDestination;
  isCodeAutoUse: boolean;
  redeemData: RedeemResponse;
  clear: () => void;
};

export type AfterUse = {
  useData: UseCampaignResponse;
  clear: () => void;
};

export type CampaignDetailState = {
  data: CampaignDetail | null;
  style: Style | null;
  subStyle: Style | null;
  quantity: number;
  totalPrice: number;
  deliveryAddress: Address | null;
  afterRedeem: AfterRedeem | null;
  afterUse: AfterUse | null;
  buttonState: CampaignDetailButtonState;
  campaignState: CampaignState;
  interfaceWebsite: string | null;
  isLoading: boolean;
  error: ErrorResponse | null;
  campaignId: string | null;
  pictures: string[];
  customCaption?: string;
};

export type CampaignDetailActions = {
  setError: (error: ErrorResponse) => void;
  fetchData: (id: string) => Promise<ServiceResponse<CampaignDetail>>;
  redeem: (
    addressOverride?: Address,
    pointUnit?: string,
    options?: { [key: string]: unknown }
  ) => Promise<ServiceResponse<RedeemResponse>>;
  use: () => Promise<ServiceResponse<UseCampaignResponse>>;
  addToFavorite: () => Promise<ServiceResponse<unknown>>;
  removeFromFavorite: () => Promise<ServiceResponse<unknown>>;
  setStyle: (style: Style) => void;
  setSubStyle: (style: Style) => void;
  setQuantity: (quantity: number) => void;
  setDeliveryAddress: (address: Address) => void;
  addToCart: () => Promise<ServiceResponse<CartCountResponse>>;
  clear: () => void;

  updateButtonState: () => void;
  checkCondition: () => void;
  calculatePrice: () => void;
};

export type CampaignDetailStore = CampaignDetailState & CampaignDetailActions;

export const createCampaignDetailStore = (): UseBoundStore<StoreApi<CampaignDetailStore>> => {
  return create<CampaignDetailStore>((set, get) => {
    return {
      data: null,
      style: null,
      subStyle: null,
      quantity: 1,
      totalPrice: 0,
      deliveryAddress: null,
      afterRedeem: null,
      afterUse: null,
      buttonState: { enabled: false, type: 'redeem', display: false },
      campaignState: 'redeemable',
      interfaceWebsite: null,
      isLoading: false,
      error: null,
      campaignId: null,
      pictures: [],
      customCaption: undefined,

      setError: (error: ErrorResponse) => set({ error }),

      fetchData: async (id: string) => {
        set({ isLoading: true, campaignId: id });
        const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
        if (!bzbsService) {
          const errorResponse = { type: 'client-error', message: 'Service not configured' } as ErrorResponse;
          set({ error: errorResponse, isLoading: false });
          return errorResponse as ServiceResponse<CampaignDetail>;
        }

        try {
          await Promise.all([
            useUserStore.getState().fetchUser(),
            useUserStore.getState().fetchPoint(),
            useUserStore.getState().fetchCartCount(),
          ]);

          const response = await bzbsService.campaignApi.campaignDetails({ id });
          if (response.type === 'success') {
            const pictures = response.model.Pictures?.map((picture) => largeImage(picture.FullImageUrl ?? '')) ?? [];
            if (pictures.length === 0) {
              pictures.push(largeImage(response.model.FullImageUrl ?? ''));
            }
            set({
              data: response.model,
              error: null,
              isLoading: false,
              pictures,
              customCaption: response.model.CustomCaption ?? undefined,
            });
            trackCommonEvents.viewCampaign();

            get().calculatePrice();
            get().updateButtonState();
            get().checkCondition();
          } else {
            set({ error: response, isLoading: false });
          }
          return response;
        } catch (error) {
          console.error(error);
          const errorResponse = { type: 'client-error', message: 'Network error' } as ErrorResponse;
          set({ error: errorResponse, isLoading: false });
          return errorResponse as ServiceResponse<CampaignDetail>;
        }
      },

      redeem: async (addressOverride?: Address, pointUnit?: string, options?: { [key: string]: unknown }) => {
        const { campaignId, style, subStyle, quantity, deliveryAddress, data } = get();

        const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
        if (!bzbsService) {
          return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<RedeemResponse>;
        }

        if (!data) {
          return { type: 'client-error', message: 'No data' } as ServiceResponse<RedeemResponse>;
        }

        const isDonation = data?.Type === campaignType.donate;
        let pointsPerUnit = data?.PointPerUnit ?? 0;
        if (pointUnit) {
          const service = data?.PointServices?.[pointUnit];
          if (service) {
            pointsPerUnit = service.AmountPerUnit ?? 0;
          }
        }

        set({ isLoading: true });
        const response = await bzbsService.campaignApi.redeem({
          id: subStyle?.campaignId ?? style?.campaignId ?? campaignId!,
          addressKey: addressOverride?.RowKey ?? deliveryAddress?.RowKey,
          contactNumber: addressOverride?.ContactNumber ?? deliveryAddress?.ContactNumber,
          spPoints: isDonation ? pointsPerUnit * quantity : undefined,
          pointUnit: pointUnit ? (data?.PointServices?.[pointUnit]?.Unit ?? undefined) : undefined,
          options,
        });
        set({ isLoading: false });

        if (response.type === 'success') {
          let destination: AfterRedeemDestination = 'code';

          if (response.model.PointType === campaignPointType.use) {
            if (data?.Delivered) {
              destination = 'delivery';
            } else if (data?.Type === campaignType.draw) {
              destination = 'draw';
            }
          } else if (response.model.PointType === campaignPointType.get) {
            destination = 'get_points';
          }

          set({
            afterRedeem: {
              redeemData: response.model,
              isCodeAutoUse: isCodeAutoUse(data, response.model),
              clear: () => set({ afterRedeem: null }),
              destination,
            },
            error: null,
          });
          trackCommonEvents.redeemSuccess(campaignId ?? '');

          get().fetchData(campaignId!);
        } else {
          set({ error: response });
        }

        return response;
      },

      use: async () => {
        const { afterRedeem, campaignId } = get();

        if (!afterRedeem?.redeemData || !afterRedeem.redeemData.RedeemKey) {
          return { type: 'client-error', message: 'No redeem data' } as ServiceResponse<UseCampaignResponse>;
        }

        const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
        if (!bzbsService) {
          return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<UseCampaignResponse>;
        }

        set({ isLoading: true });
        const response = await bzbsService.historyApi.use({
          redeemKey: afterRedeem.redeemData.RedeemKey,
        });
        set({ isLoading: false });

        if (response.type === 'success') {
          set({
            afterUse: {
              useData: response.model,
              clear: () => set({ afterRedeem: null, afterUse: null }),
            },
            error: null,
          });

          if (campaignId) {
            get().fetchData(campaignId);
          }
        } else {
          set({ error: response });
        }

        return response;
      },

      addToFavorite: async () => {
        const { campaignId } = get();
        const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
        if (!bzbsService || !campaignId) {
          return { type: 'client-error', message: 'Service not configured or no campaign selected' };
        }

        set({ isLoading: true });
        const response = await bzbsService.campaignApi.addToFavorite(campaignId);
        set({ isLoading: false });

        if (response.type === 'success') {
          set({ error: null });
          get().fetchData(campaignId);
        } else {
          set({ error: response });
        }

        return response;
      },

      removeFromFavorite: async () => {
        const { campaignId } = get();
        const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
        if (!bzbsService || !campaignId) {
          return { type: 'client-error', message: 'Service not configured or no campaign selected' };
        }

        set({ isLoading: true });
        const response = await bzbsService.campaignApi.removeFromFavorite({ id: campaignId });
        set({ isLoading: false });

        if (response.type === 'success') {
          set({ error: null });
          get().fetchData(campaignId);
        } else {
          set({ error: response });
        }

        return response;
      },

      setStyle: (style: Style) => {
        set({ style });
        get().calculatePrice();
        get().updateButtonState();
      },

      setSubStyle: (style: Style) => {
        set({ subStyle: style });
        get().calculatePrice();
        get().updateButtonState();
      },

      setQuantity: (quantity: number) => {
        set({ quantity });
        get().calculatePrice();
        get().updateButtonState();
      },

      setDeliveryAddress: (address: Address) => {
        set({ deliveryAddress: address });
      },

      addToCart: async () => {
        const { campaignId, style, subStyle, data, quantity } = get();

        if (!data) {
          return { type: 'client-error', message: 'No data' } as ServiceResponse<CartCountResponse>;
        }

        const type = data.Type ?? 0;
        if (type !== campaignType.buy) {
          return {
            type: 'client-error',
            message: 'This campaign is not add to cart type',
          } as ServiceResponse<CartCountResponse>;
        }

        const id = subStyle?.campaignId ?? style?.campaignId ?? data.ID?.toString();
        if (!id) {
          return { type: 'client-error', message: 'No campaign id' } as ServiceResponse<CartCountResponse>;
        }

        const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
        if (!bzbsService) {
          return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<CartCountResponse>;
        }

        const response = await bzbsService.cartApi.addCart({
          id,
          mode: 'add',
          qty: quantity,
        });

        if (response.type === 'success') {
          set({ error: null });
          if (campaignId) {
            get().fetchData(campaignId);
          }
        } else {
          set({ error: response });
        }

        return response;
      },

      clear: () => {
        set({
          data: null,
          style: null,
          subStyle: null,
          quantity: 1,
          totalPrice: 0,
          deliveryAddress: null,
          afterRedeem: null,
          afterUse: null,
          buttonState: { enabled: false, type: 'redeem', display: false },
          campaignState: 'redeemable',
          interfaceWebsite: null,
          error: null,
          campaignId: null,
          isLoading: false,
        });
      },

      updateButtonState: () => {
        const { data, style, subStyle, quantity } = get();
        const userPoints = useUserStore.getState().points;
        const pointServiceBalance = useUserStore.getState().pointServiceBalance;
        const supportPointUnits = useBuzzebeesAppStore.getState().config.supportPointUnits;

        if (!data) {
          set({ buttonState: { enabled: false, type: 'redeem', display: false } });
          return;
        }

        const type = data.Type ?? campaignType.free;
        const pointType = data.PointType ?? campaignPointType.use;

        let buttonType: CampaignButtonType = 'redeem';
        let display = true;
        let canAddToCart = true;

        const nonRedeemable = [campaignType.event, campaignType.media, campaignType.news];
        const skipPointCheck = type === campaignType.buy || nonRedeemable.includes(type);

        let pointEnough = true;

        if (
          !skipPointCheck &&
          pointType === campaignPointType.use &&
          !isPointEnough(data, userPoints, pointServiceBalance, supportPointUnits)
        ) {
          pointEnough = false;
        }

        switch (type) {
          case campaignType.buy:
            buttonType = 'add_to_cart';

            if (data.SubCampaignStyles?.hasStyle) {
              if (!style || (style.subitems?.length && !subStyle)) {
                canAddToCart = false;
              }
            }
            break;
          case campaignType.interface:
            switch (data.InterfaceDisplay) {
              case campaignInterfaceType.survey:
              case campaignInterfaceType.surveyApprove:
                buttonType = 'survey';
                break;
              default:
                buttonType = 'website';
                break;
            }
            set({ interfaceWebsite: data.Website ?? null });
            break;
          case campaignType.draw:
            if (data.Delivered) {
              buttonType = 'delivery';
            } else {
              buttonType = 'draw';
            }
            break;
          case campaignType.donate:
            pointEnough = !isPointEnough(data, userPoints, pointServiceBalance, supportPointUnits, quantity);
            if (data.Delivered) {
              buttonType = 'delivery';
            } else {
              buttonType = 'donate';
            }
            break;
          default:
            buttonType = pointType === campaignPointType.use ? 'redeem' : 'get_points';
            if (data.Delivered) {
              buttonType = 'delivery';
            }
            display = !nonRedeemable.includes(type);
            break;
        }

        set({
          buttonState: {
            enabled: pointEnough && canAddToCart,
            type: buttonType,
            display,
          },
        });
      },

      checkCondition: () => {
        const { data } = get();
        const supportPointUnits = useBuzzebeesAppStore.getState().config.supportPointUnits;
        const userPoints = useUserStore.getState().points;
        const pointServiceBalance = useUserStore.getState().pointServiceBalance;

        if (!data) {
          return;
        }

        let state: CampaignState = 'redeemable';

        const today = new Date();
        const timezoneOffsetMinutes = today.getTimezoneOffset();
        const currentTimeLocal = Math.floor((Date.now() - timezoneOffsetMinutes * 60 * 1000) / 1000);

        const nonRedeemable = [campaignType.event, campaignType.media, campaignType.news];
        const skipPointCheck = data.Type === campaignType.buy || nonRedeemable.includes(data.Type ?? campaignType.free);

        if (
          !skipPointCheck &&
          data.PointType === campaignPointType.use &&
          !isPointEnough(data, userPoints, pointServiceBalance, supportPointUnits)
        ) {
          state = 'point_not_enough';
        } else if (data.ItemCountSold && data.Quantity && data.ItemCountSold >= data.Quantity) {
          state = 'sold_out';
        } else if (data.RedeemMostPerPerson && data.RedeemCount && data.RedeemCount >= data.RedeemMostPerPerson) {
          state = 'redeemed';
        } else if (data.ConditionAlertId === 2) {
          state = 'redeemed';
        } else if (data.NextRedeemDate && data.NextRedeemDate > currentTimeLocal) {
          state = 'cool_down';
        } else if (data.ExpireDate && data.ExpireDate < currentTimeLocal) {
          state = 'expired';
        } else if (data.IsConditionPass === false) {
          state = 'fail_in_condition';
        }

        set({ campaignState: state });
      },

      calculatePrice: () => {
        const { data, style, subStyle, quantity } = get();
        if (!data) {
          set({ totalPrice: 0 });
          return;
        }

        const basePrice = subStyle?.price ?? style?.price ?? data?.PricePerUnit ?? 0;
        set({ totalPrice: basePrice * quantity });
      },
    };
  });
};
